// Minnesotaänger Mini App — message list content.
// Restored from Web A (4a2c0277) MessageListContent structure: per date group
// a `.sticky-date` header and a `.message-date-group` wrapping the sender-group
// chunks. withUsers=false here (Web A private-chat equivalent), so chunks are
// not wrapped in SenderGroupContainer — exactly how Web A renders a private
// chat (no sender names/avatars). Cut from the original: loading triggers,
// live-tail, account info, sponsored/forum/threads, appearance animation.

import { memo, useCallback } from '../../lib/teact/teact';

import type { TeactNode } from '../../lib/teact/teact';

import type { ApiMessage } from '../../api/types';
import type { MessageDateGroup } from './helpers/groupMessages';
import type { MiniAppPendingOperation } from '../../global/miniapp/types';

import { getMessageOriginalId, type MiniAppMessage } from '../../api/miniapp/adapter';
import { formatMessageListDate } from '../../util/localization/dateFormat';
import type { LangFn } from '../../util/localization/types';
import useOldLang from '../../hooks/useOldLang';
import buildClassName from '../../util/buildClassName';
import useLastCallback from '../../hooks/useLastCallback';

import Message from './message/Message';
import MessageListBottomMarker from './MessageListBottomMarker';
import { isAlbum, isDocumentGroup } from './helpers/groupMessages';

type OwnProps = {
  chatId: string;
  messageGroups: MessageDateGroup[];
  // Selection mode (§3.5) and pending ops (§3.4) project into every bubble.
  selectedMessageIds: string[];
  pendingOperations: MiniAppPendingOperation[];
};

function MessageListContent({ chatId, messageGroups, selectedMessageIds, pendingOperations }: OwnProps) {
  const oldLang = useOldLang();

  const renderMessageElement = useLastCallback((
    message: MiniAppMessage,
    position: { isFirstInGroup: boolean; isLastInGroup: boolean; isLastInList: boolean },
  ) => {
    // v1: all conversations are private DMs (telegram-tt `withUsers=false`),
    // so no sender-name/avatar group decoration — classic Web A private chat.
    return (
      <Message
        key={getMessageOriginalId(message)}
        chatId={chatId}
        message={message}
        isFirstInGroup={position.isFirstInGroup}
        isLastInGroup={position.isLastInGroup}
        isLastInList={position.isLastInList}
        withSenderName={false}
        senderName={undefined}
        selectedMessageIds={selectedMessageIds}
        pendingOperations={pendingOperations}
      />
    );
  });

  const calculateSenderGroups = useLastCallback((
    dateGroup: MessageDateGroup,
    dateGroupIndex: number,
    dateGroupsArray: MessageDateGroup[],
  ): TeactNode[] => {
    const senderGroupElements: TeactNode[] = [];

    dateGroup.senderGroups.forEach((senderGroup, senderGroupIndex) => {
      const senderGroupElement: TeactNode[] = [];

      senderGroup.forEach((messageOrAlbum, messageIndex) => {
        const position = {
          isFirstInGroup: messageIndex === 0,
          isLastInGroup: messageIndex === senderGroup.length - 1,
          isLastInList: senderGroupIndex === dateGroup.senderGroups.length - 1,
        };
        if (isAlbum(messageOrAlbum) || isDocumentGroup(messageOrAlbum)) {
          return;
        }
        // The adapter output guarantees the MiniAppMessage shape here.
        const message = messageOrAlbum as MiniAppMessage;
        senderGroupElement.push(renderMessageElement(message, position));
      });

      if (senderGroupElement.length) {
        senderGroupElements.push(...senderGroupElement);
      }
    });

    // v1: withUsers=false — same as Web A private chat: no SenderGroupContainer
    // wrapper (sender names/avatars stay off).
    return senderGroupElements;
  });

  const renderDateHeader = useCallback((dateGroup: MessageDateGroup) => {
    return (
      <div className="sticky-date" key={`sticky-date-${dateGroup.datetime}`}>
        {/* v1's useOldLang returns the legacy LangFn; formatMessageListDate only
            calls it as a function, so the cast is type-only. */}
        <span dir="auto">{formatMessageListDate(oldLang as unknown as LangFn, new Date(dateGroup.datetime))}</span>
      </div>
    );
  }, [oldLang]);

  const renderDateGroup = useCallback((
    dateGroup: MessageDateGroup,
    children: TeactNode[],
    className: string,
    isFirst: boolean,
  ) => {
    return (
      <div
        className={buildClassName('message-date-group', className, isFirst && 'first-message-date-group')}
        key={`date-group-${dateGroup.datetime}`}
      >
        {children}
      </div>
    );
  }, []);

  const dateGroups: TeactNode[] = [];

  messageGroups.forEach((dateGroup, dateGroupIndex, dateGroupsArray) => {
    const senderGroups = calculateSenderGroups(dateGroup, dateGroupIndex, dateGroupsArray);
    const children: TeactNode[] = [];

    children.push(renderDateHeader(dateGroup));

    senderGroups.forEach((senderGroupElement) => {
      children.push(senderGroupElement);
    });

    const shouldAddFirstClass = dateGroupIndex === 0;
    if (children.length) {
      dateGroups.push(renderDateGroup(dateGroup, children, 'before-tail', shouldAddFirstClass));
    }
  });

  return (
    <div className="messages-container">
      {dateGroups}
      <MessageListBottomMarker />
    </div>
  );
}

export default memo(MessageListContent);
